'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const areas = [
{
  name: 'Burnham Market',
  description: 'Often called "Chelsea-on-Sea" — North Norfolk\'s most prestigious village, with some of the finest period properties in the county.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_159bd975e-1782036402992.png",
  alt: 'Burnham Market village green with period properties, North Norfolk'
},
{
  name: 'Brancaster',
  description: 'Beautiful coastal village with some of North Norfolk\'s finest properties, overlooking the salt marshes and Brancaster Bay.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13667ce8b-1782036404101.png",
  alt: 'Brancaster coastal village, North Norfolk'
},
{
  name: 'Holt',
  description: 'A charming Georgian market town surrounded by stunning countryside. Home to independent shops and beautiful period architecture.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_145f76550-1782036404086.png",
  alt: 'Holt Georgian market town, North Norfolk'
},
{
  name: 'Hunstanton',
  description: 'A classic Norfolk seaside town with a wide range of property styles, from Victorian terraces to modern coastal homes.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7c4a47f-1782036404071.png",
  alt: 'Hunstanton seaside town, North Norfolk coast'
}];


const marqueeItems = [
'Burnham Market',
'Brancaster',
'Holt',
'Hunstanton',
'Wells-next-the-Sea',
'Sheringham',
'Fakenham',
'Burnham Market',
'Brancaster',
'Holt',
'Hunstanton',
'Wells-next-the-Sea',
'Sheringham',
'Fakenham'];


export default function AreasSection() {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="areas"
      className="py-32 bg-[#FAF9F6]"
      aria-labelledby="areas-heading">
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16 reveal-hidden">
          <p className="text-[#7B8A94] text-sm font-medium tracking-[0.25em] uppercase mb-4">
            Service Area
          </p>
          <h2
            id="areas-heading"
            className="font-display text-[#0F1C2E] text-4xl lg:text-5xl font-medium mb-4">
            
            Areas Covered
          </h2>
          <p className="text-[#7B8A94] text-lg max-w-xl mx-auto leading-relaxed">
            We serve homeowners across North Norfolk&apos;s most sought-after towns
            and villages.
          </p>
        </div>

        {/* Marquee */}
        <div className="overflow-hidden mb-16 border-y border-[#E8DED1] py-5">
          <div className="flex gap-16 whitespace-nowrap animate-marquee">
            {marqueeItems.map((item, i) =>
            <span
              key={`${item}-${i}`}
              className="font-display text-2xl font-medium text-[#0F1C2E]/30 tracking-wide flex-shrink-0">
              
                {item}
              </span>
            )}
          </div>
        </div>

        {/* Area cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {areas.map((area, i) =>
          <AreaCard key={area.name} area={area} index={i} />
          )}
        </div>
      </div>
    </section>);

}

function AreaCard({
  area,
  index



}: {area: typeof areas[0];index: number;}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative overflow-hidden rounded-2xl bg-white border border-[#E8DED1] shadow-sm hover:shadow-lg transition-shadow duration-300 reveal-hidden"
      style={{ transitionDelay: `${index * 100}ms` }}>
      
      <div className="aspect-[4/3] overflow-hidden">
        <AppImage
          src={area.image}
          alt={area.alt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />
        
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {/* Map pin icon */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#7B8A94"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <h3 className="font-display text-[#0F1C2E] text-xl font-semibold">
            {area.name}
          </h3>
        </div>
        <p className="text-[#7B8A94] text-sm leading-relaxed">{area.description}</p>
      </div>
    </div>);

}